<script setup lang="ts">
import ReviewCard from './ReviewCard.vue'
import FormStepNavigation from './form/FormStepNavigation.vue'
import RatingStep from './form/RatingStep.vue'
import EmploymentStatusStep from './form/EmploymentStatusStep.vue'
import TitleStep from './form/TitleStep.vue'
import ContentStep from './form/ContentStep.vue'
import RatingCriteriaStep from './form/RatingCriteriaStep.vue'
import LocationStep from './form/LocationStep.vue'
import ProsConsStep from './form/ProsConsStep.vue'
import FormActions from './form/FormActions.vue'
import ErrorSuccessStep from './form/ErrorSuccessStep.vue'

const MAX_LENGTHS = {
  title: 100,
  content: 2000,
  pros: 500,
  cons: 500,
  job_title: 50
}
const steps = [
  { id: 0, title: 'Overall Rating' },
  { id: 1, title: 'Employment Status' },
  { id: 2, title: 'Review Title' },
  { id: 3, title: 'Your Review' },
  { id: 4, title: 'Rating Details' },
  { id: 5, title: 'Location' },
  { id: 6, title: 'Pros & Cons' }
]
interface ReviewForm {
  rating: number
  title: string
  content: string
  pros: string
  cons: string
  state: string
  city: string
  current_employee: boolean
  job_title: string
  soc_code_id: string
  rating_details: { [key: string]: number }
}
interface SubmissionStatus {
  type: 'success' | 'error' | null
  message: string | null
}
const props = defineProps<{
  isSubmitting?: boolean
  initialData?: any
  isUpdate?: boolean
  permalink: string
  isInGrid?: boolean
  showPreview?: boolean
}>()
const employmentStatus = ref(props.initialData?.current_employee ?? false)
const currentStep = ref(0)
const lastValidStep = ref(0)
const errorStep = ref({
  active: false,
  type: null as 'success' | 'error' | null,
  message: null as string | null
})
const form = reactive<ReviewForm>({
  rating: props.initialData?.overall_rating || 0,
  title: props.initialData?.title || '',
  content: props.initialData?.text || '',
  pros: props.initialData?.pros || '',
  cons: props.initialData?.cons || '',
  state: props.initialData?.state || '',
  city: props.initialData?.city || '',
  current_employee: props.initialData?.current_employee ?? false,
  job_title: props.initialData?.job_title || '',
  soc_code_id: props.initialData?.soc_code_id || '',
  rating_details: props.initialData?.rating_details || {}
})
watch(
  () => props.initialData,
  (newInitialData) => {
    if (newInitialData) {
      form.rating = newInitialData.overall_rating || 0
      form.title = newInitialData.title || ''
      form.content = newInitialData.text || ''
      form.pros = newInitialData.pros || ''
      form.cons = newInitialData.cons || ''
      form.state = newInitialData.state || ''
      form.city = newInitialData.city || ''
      form.current_employee = newInitialData.current_employee ?? false
      form.job_title = newInitialData.job_title || ''
      form.soc_code_id = newInitialData.soc_code_id || ''
      form.rating_details = newInitialData.rating_details || {}
      employmentStatus.value = newInitialData.current_employee ?? false
    }
  },
  { immediate: true }
)
const emit = defineEmits(['submit', 'close'])
const previewData = computed(() => ({
  _id: 'preview',
  title: form.title || 'Your Review Title',
  text: form.content || 'Your review content will appear here...',
  overall_rating: form.rating || 0,
  pros: form.pros || 'Pros will appear here...',
  cons: form.cons || 'Cons will appear here...',
  rating_details: form.rating_details || {},
  soc_code_id: form.soc_code_id || '',
  job_title: form.job_title || 'Your Job Title',
  created_at: new Date().toISOString(),
  date: new Date(),
  company: {
    permalink: props.permalink || '',
    brand_name: props.initialData?.company?.brand_name || ''
  },
  status: 'preview'
}))
const handleClose = () => {
  emit('close')
}
watchEffect(() => {
  form.current_employee = employmentStatus.value
})
const canProceed = computed(() => {
  switch (currentStep.value) {
    case 0:
      return form.rating > 0
    case 1:
      return (
        typeof employmentStatus.value === 'boolean'
        && form.job_title.trim().length > 0
        && !!form.soc_code_id
      )
    case 2:
      return (
        form.title.trim().length >= 5 && form.title.length <= MAX_LENGTHS.title
      )
    case 3:
      return (
        form.content.trim().length >= 20
        && form.content.length <= MAX_LENGTHS.content
      )
    case 4:
      return true
    case 5:
      return !!(form.state && form.city)
    case 6:
      return (
        form.pros.trim().length >= 20
        && form.cons.trim().length >= 20
        && form.pros.length <= MAX_LENGTHS.pros
        && form.cons.length <= MAX_LENGTHS.cons
      )
    default:
      return false
  }
})
const getStepHelperText = computed(() => {
  switch (currentStep.value) {
    case 1:
      return 'Select your employment status and job role'
    case 2:
      return `Review Title (5-${MAX_LENGTHS.title} characters)`
    case 3:
      return `Review content (20-${MAX_LENGTHS.content} characters)`
    case 4:
      return 'Optional: Add specific ratings for different aspects'
    case 6:
      return `Pros & Cons (20-${MAX_LENGTHS.pros} characters each)`
    default:
      return ''
  }
})
const isLastStep = computed(() => currentStep.value === steps.length - 1)
const nextStep = () => {
  if (canProceed.value && !isLastStep.value) {
    currentStep.value++
  }
}
const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}
const submitForm = () => {
  const formData = {
    ...form,
    rating_details: { ...form.rating_details },
    review_id:
      props.isUpdate && props.initialData?._id
        ? props.initialData._id
        : undefined
  }
  emit('submit', formData)
}
const handleSubmitClick = () => {
  if (isLastStep.value && canProceed.value) {
    submitForm()
  }
}
const handleSubmissionResponse = (success: boolean, message: string) => {
  lastValidStep.value = currentStep.value
  errorStep.value = {
    active: true,
    type: success ? 'success' : 'error',
    message
  }
  currentStep.value = -1
}
const handleRetry = () => {
  errorStep.value.active = false
  currentStep.value = lastValidStep.value
}
const submitButtonText = computed(() =>
  props.isUpdate ? 'Update Review' : 'Submit Review'
)
defineExpose({ handleSubmissionResponse })
</script>

<template>
  <Card :class="['relative', { 'inline-form': isInGrid }]">
    <div class="absolute top-4 right-4 z-50">
      <Button
        color="gray"
        variant="ghost"
        icon="i-heroicons-x-mark"
        class="hover:bg-gray-100"
        aria-label="Close form"
        @click="handleClose"
      />
    </div>
    <div
      v-if="isInGrid"
      class="compact-form"
    >
      <h3 class="text-lg font-semibold mb-4">
        {{ isUpdate ? "Edit" : "Write" }} Review
      </h3>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div class="form-column">
          <FormStepNavigation
            class="mb-4"
            :steps="steps"
            :current-step="errorStep.active ? -1 : currentStep"
            :error-step="errorStep"
          />
          <ErrorSuccessStep
            v-if="errorStep.active"
            :type="errorStep.type"
            :message="errorStep.message"
            @retry="handleRetry"
          />
          <form
            v-else
            class="min-h-[400px] overflow-y-auto pr-2 space-y-4 mb-4"
            @submit.prevent="handleSubmitClick"
          >
            <RatingStep
              v-if="currentStep === 0"
              v-model="form.rating"
            />
            <EmploymentStatusStep
              v-if="currentStep === 1"
              v-model:employment-status="employmentStatus"
              v-model:job-title="form.job_title"
              v-model:soc-code-id="form.soc_code_id"
              :initial-soc-code-id="form.soc_code_id"
              :occupation-title="props.initialData?.occupation_title"
              :occupation-popular-title="
                props.initialData?.occupation_popular_title
              "
              :permalink="permalink"
            />
            <TitleStep
              v-if="currentStep === 2"
              v-model="form.title"
              :max-length="MAX_LENGTHS.title"
              :helper-text="getStepHelperText"
            />
            <ContentStep
              v-if="currentStep === 3"
              v-model="form.content"
              :max-length="MAX_LENGTHS.content"
              :helper-text="getStepHelperText"
            />
            <RatingCriteriaStep
              v-if="currentStep === 4"
              v-model:rating-details="form.rating_details"
              :helper-text="getStepHelperText"
            />
            <LocationStep
              v-if="currentStep === 5"
              v-model:state="form.state"
              v-model:city="form.city"
            />
            <ProsConsStep
              v-if="currentStep === 6"
              v-model:pros="form.pros"
              v-model:cons="form.cons"
              :max-length="MAX_LENGTHS.pros"
              :helper-text="getStepHelperText"
            />
          </form>
          <div class="form-actions">
            <FormActions
              v-if="!errorStep.active"
              :show-back="currentStep > 0"
              :is-last-step="isLastStep"
              :can-proceed="canProceed"
              :loading="isSubmitting"
              :submit-text="submitButtonText"
              @next="nextStep"
              @back="prevStep"
              @submit="handleSubmitClick"
            />
          </div>
        </div>
        <div class="preview-column hidden lg:flex lg:flex-col">
          <div class="mb-3">
            <h3 class="text-lg font-semibold">
              Preview
            </h3>
            <p class="text-sm text-gray-500">
              This is how your review will look
            </p>
          </div>
          <div class="preview-container bg-gray-50 rounded-lg p-4">
            <ReviewCard
              :review="previewData"
              :is-editable="false"
              :hide-author="true"
              class="preview-card"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      :class="[
        'flex flex-col lg:flex-row gap-4',
        { 'lg:gap-8': !isInGrid, 'lg:gap-4': isInGrid }
      ]"
    >
      <form
        :class="[
          'w-full flex-shrink-0 flex flex-col overflow-hidden',
          { 'lg:w-[450px]': !isInGrid, 'lg:flex-1': isInGrid }
        ]"
        @submit.prevent="handleSubmitClick"
      >
        <FormStepNavigation
          :class="{ 'hidden sm:block': isInGrid }"
          :steps="steps"
          :current-step="errorStep.active ? -1 : currentStep"
          :error-step="errorStep"
        />
        <div
          :class="[
            'flex-1 overflow-y-auto pr-2 space-y-4 mb-4',
            { 'max-h-[500px]': isInGrid }
          ]"
        >
          <ErrorSuccessStep
            v-if="errorStep.active"
            :type="errorStep.type"
            :message="errorStep.message"
            @retry="handleRetry"
          />
          <div
            v-else
            class="min-h-[300px]"
          >
            <RatingStep
              v-if="currentStep === 0"
              v-model="form.rating"
            />
            <EmploymentStatusStep
              v-if="currentStep === 1"
              v-model:employment-status="employmentStatus"
              v-model:job-title="form.job_title"
              v-model:soc-code-id="form.soc_code_id"
              :initial-soc-code-id="form.soc_code_id"
              :occupation-title="props.initialData?.occupation_title"
              :occupation-popular-title="
                props.initialData?.occupation_popular_title
              "
              :permalink="permalink"
            />
            <TitleStep
              v-if="currentStep === 2"
              v-model="form.title"
              :max-length="MAX_LENGTHS.title"
              :helper-text="getStepHelperText"
            />
            <ContentStep
              v-if="currentStep === 3"
              v-model="form.content"
              :max-length="MAX_LENGTHS.content"
              :helper-text="getStepHelperText"
            />
            <RatingCriteriaStep
              v-if="currentStep === 4"
              v-model:rating-details="form.rating_details"
              :helper-text="getStepHelperText"
            />
            <LocationStep
              v-if="currentStep === 5"
              v-model:state="form.state"
              v-model:city="form.city"
            />
            <ProsConsStep
              v-if="currentStep === 6"
              v-model:pros="form.pros"
              v-model:cons="form.cons"
              :max-length="MAX_LENGTHS.pros"
              :helper-text="getStepHelperText"
            />
          </div>
        </div>
        <FormActions
          v-if="!errorStep.active"
          :show-back="currentStep > 0"
          :is-last-step="isLastStep"
          :can-proceed="canProceed"
          :loading="isSubmitting"
          :submit-text="submitButtonText"
          @next="nextStep"
          @back="prevStep"
          @submit="handleSubmitClick"
        />
      </form>
      <div
        v-if="!isInGrid || showPreview"
        class="hidden lg:block flex-1 overflow-hidden px-4 pt-4"
      >
        <div class="mb-4 flex-shrink-0">
          <h3 class="text-xl font-semibold mb-4">
            Preview
          </h3>
          <p class="text-sm text-gray-500">
            This is how your review will look
          </p>
        </div>
        <div
          class="overflow-y-auto h-[calc(100%-60px)] bg-gray-50 rounded-lg p-4"
        >
          <div class="pb-8 px-2 pt-2">
            <ReviewCard
              :review="previewData"
              :is-editable="false"
              :hide-author="true"
              class="preview-card"
            />
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 transparent;
}
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}
.UCard {
  position: relative;
}
.inline-form {
  max-height: none;
}
@media (max-width: 1023px) {
  .inline-form {
    padding: 1.5rem 1rem;
  }
}
.inline-form :deep(.form-step) {
  padding: 0.5rem 0;
}
.compact-form {
  max-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
}
.compact-form .overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 transparent;
}
.inline-form.UCard {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.inline-form .compact-form {
  flex: 1;
  display: flex;
  flex-direction: column;
}
@media (max-width: 767px) {
  .preview-column {
    margin-top: 1.5rem;
  }
}
.compact-form {
  max-height: 100%;
  display: flex;
  flex-direction: column;
}
.form-column,
.preview-column {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
}
.preview-column :deep(.UCard) {
  height: 100%;
}
.inline-form.UCard {
  width: 100%;
  max-width: none;
  padding: inherit;
  background-color: white;
  border-radius: inherit;
  box-shadow: inherit;
}
.compact-form .grid {
  width: 100%;
}
.form-column,
.preview-column {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.preview-column .overflow-y-auto {
  flex: 1;
}
@media (min-width: 1024px) {
  .compact-form .max-h-\[500px\] {
    max-height: 600px;
  }
}
.form-column,
.preview-column {
  display: flex;
  flex-direction: column;
}
.form-actions {
  position: relative;
  height: 60px;
  margin-top: 1rem;
}
.preview-container {
  flex: 1;
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}
.preview-card {
  transform: scale(0.98);
  transition: all 0.2s ease;
}
.preview-card:hover {
  transform: scale(1);
}
@media (max-width: 1023px) {
  .form-column {
    width: 100%;
  }
}
.preview-column {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0.5rem;
}
</style>
